/**
 * Shared Zod/TS helpers for webapp schemas and forms (AMP-710).
 */
import { z } from 'zod';

export const validateDateYear = (val: string): boolean => {
  if (!val || val.length === 0) {
    return true;
  }
  const year = parseInt(val.substring(0, 4), 10);
  if (isNaN(year)) {
    return true; // let other validators handle format issues
  }
  return year >= 1900 && year <= 2100;
};

export const VALID_DATE_YEAR_MESSAGE = 'Year must be between 1900 and 2100';

export type Values<T> = T[keyof T];
export type OneOrMore<T> = [T, ...T[]];

export const validateMaskHHmm = (value: string) => {
  const [hourPart, minutePart] = value.split(':');
  const hours = parseInt(hourPart, 10);
  const minutes = parseInt(minutePart, 10);

  const valid =
    typeof hourPart === 'string' &&
    typeof minutePart === 'string' &&
    hourPart.length === 2 &&
    minutePart.length === 2 &&
    hours >= 0 &&
    hours <= 23 &&
    minutes >= 0 &&
    minutes <= 59;

  return valid;
};

// datetime-local inputs provide naive datetime strings without timezone (e.g., "2025-12-01T14:30")
// The backend interprets these as the property's local time based on property context in the request.
// This has to be done vs using the builtin z.string().datetime() because it expects to have a timezone specifier.
//
// validDateTime only checks that the value parses to a real date — it does NOT restrict how far in
// the past or future the date can be. Use it for fields that legitimately accept future dates
// (e.g. prepaid maintenance invoices).
export const validDateTime = (
  { invalidMsg } = {
    invalidMsg: 'Invalid Date',
  },
) => {
  return z.string().refine((val) => !isNaN(new Date(val).getTime()), { message: invalidMsg });
};

// noFutureDateTime builds on validDateTime and additionally rejects dates in the future.
export const noFutureDateTime = (
  { invalidMsg } = {
    invalidMsg: 'Invalid Date',
  },
) => {
  return validDateTime({ invalidMsg }).refine(
    (val: string) => {
      const now = Date.now();
      const date = new Date(val);
      // MUI date picker returns the date at 00:00:00, so we need to add a day to it to make sure it's not in the future
      date.setDate(date.getDate());
      const dateTime = date.getTime();
      return dateTime < now;
    },
    {
      message: 'The date cannot be in the future.',
    },
  );
};

export const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === z.ZodIssueCode.invalid_type) {
    if (
      (issue.expected === 'object' && issue.received === 'null') ||
      issue.received === 'undefined'
    ) {
      return { message: `This field is required` };
    }
  }
  return { message: ctx.defaultError };
};

export const requiredString = z.string().trim().min(1, 'Please fill out this field');

// DRF may serialize an unset DecimalField as '' rather than null. Feeding '' to
// z.coerce.number() yields 0 (Number('') === 0), which silently turns a
// genuinely-absent nullable money field into $0.00. Wrap the field schema with
// this so an empty string is treated as null before coercion runs.
// Built from `z.custom` + `.transform` + `.pipe` rather than `z.preprocess`,
// which types its input as `unknown`. That `unknown` used to be invisible;
// @hookform/resolvers v5 infers a form's field type from the schema's input,
// so it would land on every money field the UI does math on.
export const emptyStringToNull = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .custom<z.input<T> | ''>(() => true)
    .transform((value) => (value === '' ? null : value))
    .pipe(schema);
